import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useMyContext } from "@/context/ContextApi";
import { useTranslation } from "react-i18next";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const pathName = useLocation().pathname;
  const navigate = useNavigate();

  const { token, setToken, setCurrentUser, isAdmin, setIsAdmin } = useMyContext();
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("lang", lng);
  };

  const handleLogout = () => {
    localStorage.removeItem("JWT_TOKEN");
    localStorage.removeItem("USER");
    localStorage.removeItem("CSRF_TOKEN");
    localStorage.removeItem("IS_ADMIN");
    setToken(null);
    setCurrentUser(null);
    setIsAdmin(false);
    navigate("/login");
  };

  const linkClass = (path) =>
    `text-sm transition-colors duration-150 ${
      pathName === path
        ? "text-white font-semibold"
        : "text-zinc-400 hover:text-white"
    }`;

  return (
    <header className="sticky top-0 z-50 h-16 flex items-center border-b border-white/5 bg-[oklch(0.085_0.015_270)]/95 backdrop-blur-md">
      <nav className="w-full max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">

        {/* Logo */}
        <Link to="/" className="font-mono text-base font-bold tracking-tight text-violet-400 hover:text-violet-300 transition-colors">
          ft_transcendence
        </Link>

        {/* Desktop nav */}
        <ul className="hidden lg:flex items-center gap-6">
          {token && (
            <>
{/* <<<<<<< HEAD
              <li><Link to="/playerhub" className={linkClass("/playerhub")}>Player Hub</Link></li>
              <li><Link to="/create-match" className={linkClass("/create-match")}>Create Match</Link></li>
              <li><Link to="/leaderboard" className={linkClass("/leaderboard")}>Leaderboard</Link></li>
======= */}
              <li><Link to="/playerhub" className={linkClass("/playerhub")}>{t("nav_playerHub")}</Link></li>
              <li><Link to="/create-match" className={linkClass("/create-match")}>{t("nav_createMatch")}</Link></li>
              <li><Link to="/leaderboard" className={linkClass("/leaderboard")}>{t("nav_leaderboard")}</Link></li>
            </>
          )}
          <li><Link to="/about" className={linkClass("/about")}>{t("nav_about")}</Link></li>
          {token && (
            <>
              <li><Link to="/friends" className={linkClass("/friends")}>{t("nav_friends")}</Link></li>
              <li><Link to="/profile" className={linkClass("/profile")}>{t("nav_profile")}</Link></li>
              {isAdmin && (
                <li>
                  <Link
                    to="/admin/users"
                    className={`text-sm uppercase tracking-wider transition-colors duration-150 ${
                      pathName.startsWith("/admin")
                        ? "text-violet-400 font-semibold"
                        : "text-zinc-500 hover:text-violet-400"
                    }`}
                  >
                    {t("nav_admin")}
                  </Link>
                </li>
              )}
            </>
          )}
        </ul>

        {/* Desktop right action */}
        <div className="hidden lg:flex items-center gap-3">
          <select
            value={i18n.language}
            onChange={(e) => changeLanguage(e.target.value)}
            className="bg-transparent border border-white/10 text-zinc-400 text-xs rounded px-2 py-1 cursor-pointer hover:border-white/20 transition-colors"
          >
            <option value="en">EN</option>
            <option value="cs">CS</option>
            <option value="ru">RU</option>
          </select>
          {token ? (
            <button
              onClick={handleLogout}
              className="text-sm px-4 py-1.5 rounded-md border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-all duration-150"
            >
              {t("nav_logout")}
            </button>
          ) : (
            <Link
              to="/login"
              className="text-sm px-4 py-1.5 rounded-md bg-violet-600 hover:bg-violet-500 text-white font-semibold transition-colors duration-150"
            >
              {t("nav_signin")}
            </Link>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="lg:hidden text-zinc-400 hover:text-white transition-colors"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden absolute top-16 left-0 right-0 bg-[oklch(0.1_0.016_270)] border-b border-white/5 px-4 py-4 flex flex-col gap-3 shadow-lg">
          {token && (
            <>
{/* <<<<<<< HEAD
              <Link to="/playerhub" onClick={() => setMenuOpen(false)} className={linkClass("/playerhub")}>Player Hub</Link>
              <Link to="/create-match" onClick={() => setMenuOpen(false)} className={linkClass("/create-match")}>Create Match</Link>
              <Link to="/leaderboard" onClick={() => setMenuOpen(false)} className={linkClass("/leaderboard")}>Leaderboard</Link>
            </>
          )}
          <Link to="/about" onClick={() => setMenuOpen(false)} className={linkClass("/about")}>About</Link>
======= */}
              <Link to="/playerhub" onClick={() => setMenuOpen(false)} className={linkClass("/playerhub")}>{t("nav_playerHub")}</Link>
              <Link to="/create-match" onClick={() => setMenuOpen(false)} className={linkClass("/create-match")}>{t("nav_createMatch")}</Link>
              <Link to="/leaderboard" onClick={() => setMenuOpen(false)} className={linkClass("/leaderboard")}>{t("nav_leaderboard")}</Link>
            </>
          )}
          <Link to="/about" onClick={() => setMenuOpen(false)} className={linkClass("/about")}>{t("nav_about")}</Link>
          {token && (
            <>
              <Link to="/friends" onClick={() => setMenuOpen(false)} className={linkClass("/friends")}>{t("nav_friends")}</Link>
              <Link to="/profile" onClick={() => setMenuOpen(false)} className={linkClass("/profile")}>{t("nav_profile")}</Link>
              {isAdmin && (
                <Link
                  to="/admin/users"
                  onClick={() => setMenuOpen(false)}
                  className={`text-sm uppercase tracking-wider ${
                    pathName.startsWith("/admin") ? "text-violet-400 font-semibold" : "text-zinc-500 hover:text-violet-400"
                  }`}
                >
                  {t("nav_admin")}
                </Link>
              )}
              <button
                onClick={() => { setMenuOpen(false); handleLogout(); }}
                className="text-sm text-left text-zinc-400 hover:text-white transition-colors"
              >
                {t("nav_logout")}
              </button>
            </>
          )}
          {!token && (
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              className="text-sm px-4 py-2 rounded-md bg-violet-600 hover:bg-violet-500 text-white font-semibold text-center transition-colors"
            >
              {t("nav_signin")}
            </Link>
          )}
          <select
            value={i18n.language}
            onChange={(e) => changeLanguage(e.target.value)}
            className="bg-transparent border border-white/10 text-zinc-400 text-xs rounded px-2 py-1 w-fit"
          >
            <option value="en">EN</option>
            <option value="cs">CS</option>
            <option value="ru">RU</option>
          </select>
        </div>
      )}
    </header>
  );
};

export default Navbar;
